import type {
  IdeationAudienceScore,
  IdeationBenchmarkReference,
  IdeationBenchmarkReferenceWithImage,
  IdeationBenchmarkReferenceWithImageV1,
  IdeationConstraint,
  IdeationCreativeScore,
  IdeationExecution,
  IdeationExecutionWithImages,
  IdeationExecutionWithImagesV1,
  IdeationIdeaWithImages,
  IdeationIdeaWithImagesV1,
  IdeationIdeaWithScore,
  IdeationImage,
  IdeationKeyStrategicElement,
  IdeationWidget,
  // V1 types for backward compatibility
  IdeationWidgetV1,
} from '@inference.engine/widgets';
import type { WidgetRenderProps } from '@/entities/widget';

// ============================================================================
//  Schema Types - Re-export from widgets lib with shorter names
// ============================================================================

// Benchmark types
export type BenchmarkReference = IdeationBenchmarkReference;
export type BenchmarkReferenceWithImage = IdeationBenchmarkReferenceWithImage;

// Summary types (key strategic elements and constraints)
export type KeyStrategicElement = IdeationKeyStrategicElement;
export type Constraint = IdeationConstraint;

// Image type
export type { IdeationImage };

// Idea types
export type IdeationIdea = IdeationIdeaWithScore;
export type { IdeationIdeaWithImages };
export type { IdeationExecution };
export type { IdeationExecutionWithImages };
export type { IdeationAudienceScore };
export type { IdeationCreativeScore };

// Score component types
export type ScoreType = 'audience' | 'creative';

// ============================================================================
// Widget Data Types
// ============================================================================

// The schema uses Date for createdAt, but when serialized (e.g., from API),
// it becomes a string. This type adapts the schema type for runtime usage.
export interface IdeationWidgetData extends Omit<IdeationWidget, 'createdAt'> {
  createdAt: string;
  updatedAt?: string;
}

/**
 * Config-injected Start Brief entry point (assembled at `@/lib/config/agents.config`
 * from the shared picker + brief-generation flow); the widget wraps its trigger in it.
 */
export interface IdeationWidgetBriefDeps {
  StartBrief: (props: { children: React.ReactNode }) => React.ReactElement | null;
}

interface IdeationWidgetFullProps {
  data: IdeationWidgetDataUnion;
  /** Config-injected Start Brief flow. Required in full mode; dropped in thumbnail. */
  briefDeps: IdeationWidgetBriefDeps;
  /**
   * Read-only Library preview: the idea index to open on (a single saved idea
   * → its detail), or `null` for the campaign overview. Undefined in the live
   * chat, where the overview is always the entry point.
   */
  previewInitialIdeaIndex?: number | null;
}

/**
 * `mode` defaults to `'full'`, so existing call sites that pass no `mode`
 * keep requiring `briefDeps`. `mode: 'thumbnail'` drops it.
 */
export type IdeationWidgetProps = WidgetRenderProps<IdeationWidgetFullProps, 'briefDeps'>;

// ============================================================================
// Component-Friendly Types
// These types provide a structured view for components while maintaining
// compatibility with the flat schema
// ============================================================================

/**
 * Summary structure for components that need grouped summary data
 * Maps from flat fields to a structured object
 */
export interface IdeationSummary {
  creativeMission: string;
  keyStrategicElements: KeyStrategicElement[];
  constraints: Constraint[];
}

/**
 * Asset structure for components that need image data
 * Provides a consistent interface for accessing image properties
 */
export interface Asset {
  assetId: string;
  mimeType: string;
  fileName: string;
  size: number;
}

// ============================================================================
// Helper Functions - Convert flat fields to component-friendly structures
// ============================================================================

/**
 * Creates an Asset object from flat image fields
 */
export const toAsset = (
  assetId: string,
  mimeType: string,
  fileName: string,
  size: number,
): Asset => ({
  assetId,
  mimeType,
  fileName,
  size,
});

/**
 * Creates an Asset from a benchmark reference with image
 */
export const benchmarkToAsset = (ref: BenchmarkReferenceWithImage): Asset =>
  toAsset(ref.imageAssetId, ref.imageMimeType, ref.imageFileName, ref.imageSize);

/**
 * Creates an Asset from an idea with images
 */
export const ideaToAsset = (idea: IdeationIdeaWithImages): Asset =>
  toAsset(idea.imageAssetId, idea.imageMimeType, idea.imageFileName, idea.imageSize);

/**
 * Creates an Asset from an execution with images
 */
export const executionToAsset = (execution: IdeationExecutionWithImages): Asset =>
  toAsset(
    execution.imageAssetId,
    execution.imageMimeType,
    execution.imageFileName,
    execution.imageSize,
  );

/**
 * Creates an Asset from an IdeationImage
 */
export const imageToAsset = (image: IdeationImage): Asset =>
  toAsset(image.assetId, image.mimeType, image.fileName, image.size);

/**
 * Creates an IdeationSummary from flat widget data fields
 */
export const toSummary = (data: IdeationWidgetData): IdeationSummary => ({
  creativeMission: data.creativeMission,
  keyStrategicElements: data.keyStrategicElements,
  constraints: data.constraints,
});

/**
 * Creates main image Asset from widget data
 */
export const toMainImage = (data: IdeationWidgetData): Asset =>
  toAsset(data.imageAssetId, data.imageMimeType, data.imageFileName, data.imageSize);

/**
 * Creates additional images Assets array from widget data
 */
export const toAdditionalImages = (data: IdeationWidgetData): Asset[] =>
  data.additionalImages.map(imageToAsset);

// ============================================================================
// V1 to V2 Data Transformation
// Handles backward compatibility with V1 widget data format
// ============================================================================

/**
 * V1 widget data type with serialized dates
 */
export interface IdeationWidgetV1Data extends Omit<IdeationWidgetV1, 'createdAt'> {
  createdAt: string;
  updatedAt?: string;
}

/**
 * Union type for widget props that can accept either V1 or V2 data
 */
export type IdeationWidgetDataUnion = IdeationWidgetData | IdeationWidgetV1Data;

/**
 * Type guard to detect V1 data format
 * V1 data has nested `summary` and `mainImage` objects instead of flat fields
 */
export const isV1Data = (data: IdeationWidgetDataUnion): data is IdeationWidgetV1Data => {
  return 'summary' in data && 'mainImage' in data;
};

// V1 nested image (`AssetSchema`). Typed nullable on purpose: the V1/V2
// schemas mark every image required, but artifacts persisted before image
// generation finished omit it in the wild. A missing image must degrade to
// empty identifiers rather than throw — an unguarded `ref.image.assetId`
// here takes down the whole route via the error boundary (the picker renders
// a thumbnail per workspace artifact, so one bad artifact breaks the list).
type V1ImageAsset = {
  assetId: string;
  mimeType: string;
  metadata?: { fileName: string; size: number };
};

// Flattens an optional V1 image into the V2 `image*` fields.
const flattenV1ImageFields = (image: V1ImageAsset | null | undefined) => ({
  imageAssetId: image?.assetId ?? '',
  imageMimeType: image?.mimeType ?? '',
  imageFileName: image?.metadata?.fileName ?? '',
  imageSize: image?.metadata?.size ?? 0,
});

// Flattens an optional V1 image into a V2 `IdeationImage`.
const v1ImageToIdeationImage = (image: V1ImageAsset | null | undefined): IdeationImage => ({
  assetId: image?.assetId ?? '',
  mimeType: image?.mimeType ?? '',
  fileName: image?.metadata?.fileName ?? '',
  size: image?.metadata?.size ?? 0,
});

/**
 * Transforms V1 benchmark reference to V2 format (flatten image object)
 */
const transformBenchmarkV1ToV2 = (
  ref: IdeationBenchmarkReferenceWithImageV1,
): IdeationBenchmarkReferenceWithImage => ({
  type: ref.type,
  title: ref.title,
  description: ref.description,
  universalTruth: ref.universalTruth,
  brand: ref.brand,
  agency: ref.agency,
  year: ref.year,
  link: ref.link,
  ...flattenV1ImageFields(ref.image),
});

/**
 * Transforms V1 execution to V2 format (flatten image object)
 */
const transformExecutionV1ToV2 = (
  execution: IdeationExecutionWithImagesV1,
): IdeationExecutionWithImages => ({
  title: execution.title,
  description: execution.description,
  platform: execution.platform,
  ...flattenV1ImageFields(execution.image),
});

/**
 * Transforms V1 idea to V2 format (flatten image objects)
 */
const transformIdeaV1ToV2 = (idea: IdeationIdeaWithImagesV1): IdeationIdeaWithImages => ({
  id: idea.id,
  title: idea.title,
  description: idea.description,
  provocation: idea.provocation,
  whyThisWorks: idea.whyThisWorks,
  audienceScore: idea.audienceScore,
  creativeScore: idea.creativeScore,
  ...flattenV1ImageFields(idea.image),
  executions: idea.executions.map(transformExecutionV1ToV2),
});

/**
 * Transforms V1 widget data to V2 format
 * - Flattens summary object to top-level fields
 * - Converts mainImage Asset to flat image fields
 * - Transforms additionalImages from metadata wrapper format
 * - Transforms references, ideas, and executions image fields
 */
export const transformV1ToV2 = (v1Data: IdeationWidgetV1Data): IdeationWidgetData => ({
  title: v1Data.title,
  coreTask: v1Data.coreTask,
  // Flatten summary object
  creativeMission: v1Data.summary.creativeMission,
  keyStrategicElements: v1Data.summary.keyStrategicElements,
  constraints: v1Data.summary.constraints,
  // Flatten mainImage Asset to flat fields
  ...flattenV1ImageFields(v1Data.mainImage),
  // Transform additionalImages from metadata wrapper format
  additionalImages: v1Data.additionalImages.map(v1ImageToIdeationImage),
  // Transform references with flattened image fields
  references: v1Data.references.map(transformBenchmarkV1ToV2),
  // V1 sparks are compatible with V2
  sparks: v1Data.sparks,
  // Transform ideas with flattened image fields
  ideas: v1Data.ideas.map(transformIdeaV1ToV2),
  // Preserve timestamps
  createdAt: v1Data.createdAt,
  updatedAt: v1Data.updatedAt,
});

/**
 * Normalizes widget data to V2 format
 * If data is already V2, returns as-is; if V1, transforms to V2
 */
export const normalizeWidgetData = (data: IdeationWidgetDataUnion): IdeationWidgetData => {
  if (isV1Data(data)) {
    return transformV1ToV2(data);
  }
  return data;
};
